import React, { useEffect, useState } from 'react';
import { getBillboardHealth, type BillboardHealthSnapshot } from '../../api/billboard';

export const BillboardHealthCard: React.FC = () => {
  const [health, setHealth] = useState<BillboardHealthSnapshot | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadHealth = async () => {
    setLoading(true);
    setError(null);
    try {
      const snapshot = await getBillboardHealth();
      setHealth(snapshot);
    } catch {
      setError('Unable to load billboard health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadHealth();
  }, []);

  const formatTimestamp = (iso: string | null): string => {
    if (!iso) return 'No telemetry yet';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleString();
  };

  const deriveStatus = (snapshot: BillboardHealthSnapshot | null): {
    label: string;
    tone: 'ok' | 'warn';
  } => {
    if (!snapshot || !snapshot.lastTelemetryAt) {
      return { label: 'No recent telemetry', tone: 'warn' };
    }

    const reasons = snapshot.lastReasons || [];
    const hasCritical = reasons.includes('GLOBAL_FORCE_BLANK') || reasons.includes('OVER_SPEED_LIMIT');

    if (hasCritical) {
      return { label: 'Attention required', tone: 'warn' };
    }

    return { label: 'Healthy', tone: 'ok' };
  };

  const status = deriveStatus(health);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Billboard health</h2>
          <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
            Last telemetry snapshot from connected billboard players.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadHealth()}
          disabled={loading}
          className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 disabled:opacity-60"
       >
          Refresh
        </button>
      </div>

      {loading && !error && (
        <p className="text-xs text-slate-500 dark:text-slate-400">Loading latest telemetry…</p>
      )}
      {error && !loading && <p className="text-xs text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="space-y-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500 dark:text-slate-400">Status</p>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                status.tone === 'ok'
                  ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                  : 'bg-amber-50 text-amber-700 ring-1 ring-amber-100'
              }`}
            >
              {status.label}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs text-slate-700 dark:text-slate-300">
            <div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Last telemetry</p>
              <p className="mt-0.5 break-words text-[11px]">
                {formatTimestamp(health?.lastTelemetryAt ?? null)}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Last speed</p>
              <p className="mt-0.5 text-sm font-medium">
                {typeof health?.lastSpeedMph === 'number' ? `${health.lastSpeedMph} mph` : '—'}
              </p>
            </div>
          </div>

          <div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">Last safety reasons</p>
            <p className="mt-0.5 text-xs">
              {health && health.lastReasons && health.lastReasons.length > 0
                ? health.lastReasons.join(', ')
                : 'None reported'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillboardHealthCard;
